type KiotaApiError = {
  additionalData?: unknown;
  detail?: unknown;
  errors?: unknown;
  message?: unknown;
  reasonError?: unknown;
  responseStatusCode?: unknown;
  status?: unknown;
  title?: unknown;
};

const STATUS_MESSAGES: Record<number, string> = {
  401: 'Authentication error. Please refresh and try again.',
  409: 'Data has been changed in the meantime. Please refresh and try again.',
  500: 'Oops! Something went wrong on our end. Please try again later.',
};

const ERROR_METADATA_KEYS = new Set([
  'additionaldata',
  'cause',
  'detail',
  'errors',
  'headers',
  'message',
  'name',
  'reasonerror',
  'responseheaders',
  'responsestatuscode',
  'stack',
  'status',
  'title',
  'traceid',
  'type',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeMessages(messages: string[]): string[] {
  return Array.from(new Set(messages.map(message => message.trim()).filter(Boolean)));
}

function extractValidationMessages(data: unknown, includeStringValues = true): string[] {
  if (typeof data === 'string') {
    return includeStringValues ? normalizeMessages([data]) : [];
  }

  if (Array.isArray(data)) {
    return normalizeMessages(data.flatMap(value => {
      if (typeof value === 'string') {
        return [value];
      }

      if (!isRecord(value)) {
        return [];
      }

      const itemMessage = [value.reasonError, value.detail, value.message]
        .find((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);

      return itemMessage ? [itemMessage] : extractValidationMessages(value, false);
    }));
  }

  if (!isRecord(data)) return [];

  return normalizeMessages(Object.values(data).flatMap(value => {
    if (Array.isArray(value) || isRecord(value)) {
      return extractValidationMessages(value, includeStringValues);
    }

    return includeStringValues && typeof value === 'string' ? [value] : [];
  }));
}

function extractObjectValidationMessages(data: unknown): string[] {
  if (!isRecord(data)) return [];

  return normalizeMessages(Object.entries(data).flatMap(([key, value]) => {
    if (ERROR_METADATA_KEYS.has(key.toLowerCase())) {
      return [];
    }

    if (Array.isArray(value)) {
      return extractValidationMessages(value);
    }

    return isRecord(value) ? extractObjectValidationMessages(value) : [];
  }));
}

/** Returns the HTTP status exposed by a Kiota API error, when available. */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (!isRecord(error)) return undefined;

  const apiError = error as KiotaApiError;
  const status = Number(apiError.responseStatusCode ?? apiError.status);
  return Number.isFinite(status) && status > 0 ? status : undefined;
}

/** Extracts user-facing messages from Kiota API errors and ordinary errors. */
export function getApiErrorMessages(
  error: unknown,
  fallbackMessage = 'An unexpected error occurred.'
): string[] {
  if (typeof error === 'string') return normalizeMessages([error]);
  if (Array.isArray(error)) {
    const messages = extractValidationMessages(error);
    return messages.length > 0 ? messages : [fallbackMessage];
  }
  if (!isRecord(error)) return [fallbackMessage];

  const apiError = error as KiotaApiError;
  const additionalData = apiError.additionalData;
  const nestedErrors = isRecord(additionalData) ? additionalData.errors : undefined;

  const validationMessages = extractValidationMessages(apiError.errors ?? nestedErrors);
  if (validationMessages.length > 0) return validationMessages;

  // Kiota stores unknown response properties in additionalData. The API's
  // root-level { "": ["message"] } validation response is represented here.
  const additionalMessages = extractObjectValidationMessages(additionalData);
  if (additionalMessages.length > 0) return additionalMessages;

  // Also accept a deserialized validation object directly.
  const directMessages = extractObjectValidationMessages(apiError);
  if (directMessages.length > 0) return directMessages;

  const detailedMessage = [apiError.detail, apiError.reasonError]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  if (detailedMessage) return [detailedMessage.trim()];

  const status = getApiErrorStatus(apiError);
  if (status !== undefined && STATUS_MESSAGES[status]) {
    return [STATUS_MESSAGES[status]];
  }

  const genericMessage = [apiError.message, apiError.title]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0);
  return [genericMessage?.trim() || fallbackMessage];
}
